import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import ApiClient from '../client';
import { ENDPOINTS } from '../endpoints';
import { CreateUserDto, UpdateUserDto, InviteUserDto, UserListParams } from '../types';
import { queryKeys } from '../keys';

const client = new ApiClient(process.env.API_URL!, 'app');

// Queries
export const useUsers = (params?: UserListParams) =>
  useQuery({
    queryKey: queryKeys.users(params),
    queryFn: () => client.get(ENDPOINTS.USERS, { params }),
  });

export const useUserProfile = () =>
  useQuery({
    queryKey: queryKeys.userProfile,
    queryFn: () => client.get(ENDPOINTS.AUTH_ME),
  });

// Mutations
export const useCreateUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateUserDto) => client.post(ENDPOINTS.USERS, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: UpdateUserDto }) =>
      client.patch(ENDPOINTS.USER(userId), data),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.user(userId) });
    },
  });
};

export const useSendUserInvite = () =>
  useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: InviteUserDto }) =>
      client.post(ENDPOINTS.USER_INVITE(userId), data),
  });

export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => client.delete(ENDPOINTS.USER(userId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};
